/**
 * NutrientProfileChart.tsx
 * 
 * Component for visualizing nutrient profiles with comparison capabilities.
 * This chart displays target vs. actual nutrient levels and highlights
 * deficiencies or excesses.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  Divider,
  Tooltip,
  CircularProgress,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import ReactEcharts from 'echarts-for-react';
import { NutrientProfile } from '../../types/nutrient';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import { useMediaQuery } from '@mui/material';

// Define thresholds for warning/error colors (% variance)
const ERROR_THRESHOLD = 20;
const WARNING_THRESHOLD = 10;

interface NutrientData {
  element: string;
  target: number;
  actual: number;
  percentVariance: number;
  unit: string;
  category: 'macro' | 'micro' | 'other';
}

interface NutrientProfileChartProps {
  title?: string;
  targetProfile?: NutrientProfile;
  actualProfile?: NutrientProfile;
  loading?: boolean;
  error?: string | null;
  allowDownload?: boolean;
  enableToggleView?: boolean;
  onSaveChart?: (dataUrl: string) => void;
}

/**
 * Groups nutrients into macro and micronutrients
 */
const categorizeNutrients = (element: string): 'macro' | 'micro' | 'other' => {
  const macronutrients = ['N', 'P', 'K', 'Ca', 'Mg', 'S'];
  const micronutrients = ['Fe', 'Mn', 'Cu', 'Zn', 'B', 'Mo', 'Cl'];
  
  if (macronutrients.includes(element)) return 'macro';
  if (micronutrients.includes(element)) return 'micro';
  return 'other';
};

const NutrientProfileChart: React.FC<NutrientProfileChartProps> = ({
  title = 'Nutrient Profile',
  targetProfile,
  actualProfile,
  loading = false,
  error = null,
  allowDownload = true,
  enableToggleView = true,
  onSaveChart,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  const [chartType, setChartType] = useState<'bar' | 'radar'>('bar');
  const [displayUnit, setDisplayUnit] = useState<string>('ppm');
  const [showVariance, setShowVariance] = useState<boolean>(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [chartInstance, setChartInstance] = useState<any>(null);
  
  // Prepare chart data by combining target and actual profiles
  const chartData = useMemo(() => {
    if (!targetProfile) {
      return [];
    }
    
    const data: NutrientData[] = [];
      Object.entries(targetProfile).forEach(([element, target]) => {
      // Only process numeric values, skip properties like name, elementSymbol, etc.
      if (typeof target === 'number') {
        const actual = actualProfile && typeof actualProfile[element] === 'number' ? 
          actualProfile[element] as number : 0;
        const numTarget = target as number;
        const variance = numTarget > 0 ? ((actual - numTarget) / numTarget) * 100 : 0;
        
        data.push({
          element,
          target: numTarget,
          actual,
          percentVariance: parseFloat(variance.toFixed(1)),
          unit: displayUnit,
          category: categorizeNutrients(element),
        });
      }
    });
      // Add elements that are in actual but not in target
    if (actualProfile) {
      Object.entries(actualProfile).forEach(([element, actual]) => {
        if (typeof actual === 'number' && !targetProfile[element]) {
          data.push({
            element,
            target: 0,
            actual: actual as number,
            percentVariance: 100,
            unit: displayUnit,
            category: categorizeNutrients(element),
          });
        }
      });
    }
    
    return data;
  }, [targetProfile, actualProfile, displayUnit]);
  
  // Filter chart data based on selected category
  const filteredChartData = useMemo(() => {
    if (selectedCategory === 'all') {
      return chartData;
    }
    if (selectedCategory === 'macro') {
      return chartData.filter(item => item.category === 'macro');
    }
    if (selectedCategory === 'micro') {
      return chartData.filter(item => item.category === 'micro');
    }
    return chartData;
  }, [chartData, selectedCategory]);
  
  // Generate ECharts options
  const getChartOptions = () => {
    if (filteredChartData.length === 0) {
      return {
        title: {
          text: 'No Data Available',
          left: 'center',
        }
      };
    }
    
    const elements = filteredChartData.map(item => item.element);
    const targetValues = filteredChartData.map(item => item.target);
    const actualValues = filteredChartData.map(item => item.actual);
    const variances = filteredChartData.map(item => item.percentVariance);
    
    // Generate colors based on variance
    const itemColors = filteredChartData.map(item => {
      const variance = Math.abs(item.percentVariance);
      
      if (variance > ERROR_THRESHOLD) return theme.palette.error.main;
      if (variance > WARNING_THRESHOLD) return theme.palette.warning.main;
      return theme.palette.success.main;
    });
    
    // Bar chart options
    if (chartType === 'bar') {
      return {
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            const elementIndex = params[0].dataIndex;
            const element = filteredChartData[elementIndex].element;
            const target = filteredChartData[elementIndex].target;
            const actual = filteredChartData[elementIndex].actual;
            const variance = filteredChartData[elementIndex].percentVariance;
            
            return `
              <strong>${element}</strong><br />
              Target: ${target} ${displayUnit}<br />
              Actual: ${actual} ${displayUnit}<br />
              ${showVariance ? `Variance: ${variance}%` : ''}
            `;
          }
        },
        legend: {
          data: ['Target', 'Actual'],
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: elements,
          axisLabel: {
            interval: 0,
            rotate: isSmallScreen ? 45 : 0
          }
        },
        yAxis: {
          type: 'value',
          name: displayUnit,
          nameLocation: 'end'
        },
        series: [
          {
            name: 'Target',
            type: 'bar',
            data: targetValues,
            itemStyle: {
              color: theme.palette.primary.main
            },
            emphasis: {
              itemStyle: {
                color: theme.palette.primary.dark
              }
            }
          },
          {
            name: 'Actual',
            type: 'bar',
            data: actualValues,
            itemStyle: {
              color: (params: any) => {
                return itemColors[params.dataIndex];
              }
            }
          }
        ]
      };
    }
    
    // Radar chart options
    return {
      tooltip: {
        trigger: 'item'
      },
      legend: {
        data: ['Target', 'Actual'],
        bottom: 0
      },
      radar: {
        indicator: elements.map((element, index) => {
          const maxValue = Math.max(targetValues[index], actualValues[index]) * 1.2;
          return { name: element, max: maxValue };
        }),
        radius: '65%'
      },
      series: [
        {
          type: 'radar',
          name: 'Nutrient Profile',
          data: [
            {
              value: targetValues,
              name: 'Target',
              itemStyle: {
                color: theme.palette.primary.main
              },
              areaStyle: {
                color: `rgba(${parseInt(theme.palette.primary.main.slice(1, 3), 16)}, 
                          ${parseInt(theme.palette.primary.main.slice(3, 5), 16)}, 
                          ${parseInt(theme.palette.primary.main.slice(5, 7), 16)}, 0.2)`
              }
            },
            {
              value: actualValues,
              name: 'Actual',
              itemStyle: {
                color: theme.palette.secondary.main
              },
              areaStyle: {
                color: `rgba(${parseInt(theme.palette.secondary.main.slice(1, 3), 16)}, 
                          ${parseInt(theme.palette.secondary.main.slice(3, 5), 16)}, 
                          ${parseInt(theme.palette.secondary.main.slice(5, 7), 16)}, 0.2)`
              }
            }
          ]
        }
      ]
    };
  };
  
  // Handle chart type change
  const handleChartTypeChange = (event: SelectChangeEvent) => {
    setChartType(event.target.value as 'bar' | 'radar');
  };
  
  // Handle unit change
  const handleUnitChange = (event: SelectChangeEvent) => {
    setDisplayUnit(event.target.value);
  };
  
  // Handle category filter change
  const handleCategoryChange = (event: SelectChangeEvent) => {
    setSelectedCategory(event.target.value);
  };
  
  // Toggle variance display
  const handleVarianceToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowVariance(event.target.checked);
  };
  
  // Save chart as image
  const handleSave = () => {
    if (chartInstance && onSaveChart) {
      const dataUrl = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      });
      onSaveChart(dataUrl);
    }
  };
  
  // Handle chart ready event
  const onChartReady = (instance: any) => {
    setChartInstance(instance);
  };
  
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          
          {allowDownload && chartInstance && (
            <Tooltip title="Download Chart">
              <DownloadIcon 
                onClick={handleSave} 
                sx={{ cursor: 'pointer' }} 
                color="primary"
              />
            </Tooltip>
          )}
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Chart Controls */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          {enableToggleView && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="chart-type-select-label">Chart Type</InputLabel>
              <Select
                labelId="chart-type-select-label"
                id="chart-type-select"
                value={chartType}
                label="Chart Type"
                onChange={handleChartTypeChange}
              >
                <MenuItem value="bar">Bar Chart</MenuItem>
                <MenuItem value="radar">Radar Chart</MenuItem>
              </Select>
            </FormControl>
          )}
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="unit-select-label">Unit</InputLabel>
            <Select
              labelId="unit-select-label"
              id="unit-select"
              value={displayUnit}
              label="Unit"
              onChange={handleUnitChange}
            >
              <MenuItem value="ppm">ppm</MenuItem>
              <MenuItem value="mg/L">mg/L</MenuItem>
              <MenuItem value="mmol/L">mmol/L</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="category-select-label">Category</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={selectedCategory}
              label="Category"
              onChange={handleCategoryChange}
            >
              <MenuItem value="all">All Elements</MenuItem>
              <MenuItem value="macro">Macronutrients</MenuItem>
              <MenuItem value="micro">Micronutrients</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={showVariance}
                onChange={handleVarianceToggle}
                size="small"
              />
            }
            label="Show Variance"
          />
        </Box>
        
        {/* Chart Container */}
        <Paper 
          variant="outlined" 
          sx={{ 
            height: 400, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative' 
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography color="error" variant="body1">
                {error}
              </Typography>
            </Box>
          ) : (!targetProfile && !actualProfile) ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No nutrient data available. Select profiles to compare.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: '100%' }}>
              <ReactEcharts
                option={getChartOptions()}
                style={{ height: '100%', width: '100%' }}
                onChartReady={onChartReady}
              />
            </Box>
          )}
        </Paper>
        
        {/* Legend for variance colors */}
        {showVariance && !loading && !error && targetProfile && actualProfile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.success.main, mr: 1 }} />
              <Typography variant="caption">
                Variance &lt;{WARNING_THRESHOLD}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.warning.main, mr: 1 }} />
              <Typography variant="caption">
                Variance {WARNING_THRESHOLD}-{ERROR_THRESHOLD}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.error.main, mr: 1 }} />
              <Typography variant="caption">
                Variance &gt;{ERROR_THRESHOLD}%
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NutrientProfileChart;
